import { useCallback } from 'react'
import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    query,
    where,
    orderBy,
    Timestamp,
    serverTimestamp,
} from 'firebase/firestore'
import { firestore, auth } from '../config/firebase'
import { IVehicleService, VehicleService, VehicleServiceStatus } from '../../../shared/types/vehicle-service.types'
import { COLLECTIONS } from '../../../shared/constants/collections'

export interface VehicleServiceFilters {
    /** Filtruje dle VIN vozidla. */
    vehicleUid?: string
    /** Filtruje dle stavu — jeden status nebo pole statusů (použije 'in' operátor). */
    status?: VehicleServiceStatus | VehicleServiceStatus[]
    /** Filtruje dle servisního střediska (ID z číselníku 'dealers'). */
    dealerId?: string
}

type VehicleServiceUpdater = Partial<Omit<IVehicleService, 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>>

interface IUseVehicleServiceReturn {
    get: (id: string) => Promise<VehicleService | null>
    list: (filters?: VehicleServiceFilters) => Promise<VehicleService[]>
    create: (data: Omit<IVehicleService, 'createdAt'>) => Promise<VehicleService>
    edit: (id: string, data: VehicleServiceUpdater) => Promise<void>
}

const convertDoc = (id: string, data: Record<string, unknown>): VehicleService => ({
    id,
    ...data,
    createdAt: data.createdAt instanceof Timestamp
        ? data.createdAt.toDate()
        : new Date(data.createdAt as string),
    ...(data.finishedAt ? {
        finishedAt: data.finishedAt instanceof Timestamp
            ? data.finishedAt.toDate()
            : new Date(data.finishedAt as string),
    } : {}),
} as VehicleService)

const useVehicleService = (): IUseVehicleServiceReturn => {

    /**
     * Načte jeden servisní záznam podle jeho ID.
     * @param id - Firestore document ID servisu
     */
    const get = useCallback(async (id: string): Promise<VehicleService | null> => {
        const snapshot = await getDoc(doc(firestore, COLLECTIONS.VEHICLE_SERVICE, id))
        if (!snapshot.exists()) return null
        return convertDoc(snapshot.id, snapshot.data())
    }, [])

    /**
     * Načte seznam servisních záznamů s volitelnými filtry.
     * Výsledky jsou řazeny sestupně dle data vytvoření.
     * @param filters - Volitelné filtry: vehicleUid, garageUid, status
     */
    const list = useCallback(async (filters?: VehicleServiceFilters): Promise<VehicleService[]> => {
        const constraints = []

        if (filters?.vehicleUid) {
            constraints.push(where('vehicleUid', '==', filters.vehicleUid))
        }

        if (filters?.status !== undefined) {
            if (Array.isArray(filters.status)) {
                constraints.push(where('status', 'in', filters.status))
            } else {
                constraints.push(where('status', '==', filters.status))
            }
        }

        if (filters?.dealerId) {
            constraints.push(where('dealerId', '==', filters.dealerId))
        }

        constraints.push(orderBy('createdAt', 'desc'))

        const q = query(collection(firestore, COLLECTIONS.VEHICLE_SERVICE), ...constraints)
        const snapshot = await getDocs(q)
        return snapshot.docs.map((d) => convertDoc(d.id, d.data()))
    }, [])

    /**
     * Vytvoří nový servisní záznam. Pole `createdAt` je nastaveno serverem.
     * @param data - Data servisu bez `createdAt`
     */
    const create = useCallback(async (data: Omit<IVehicleService, 'createdAt'>): Promise<VehicleService> => {
        const ref = await addDoc(collection(firestore, COLLECTIONS.VEHICLE_SERVICE), {
            ...data,
            createdAt: serverTimestamp(),
        })
        return { id: ref.id, ...data, createdAt: new Date() }
    }, [])

    /**
     * Aktualizuje pole existujícího servisního záznamu.
     * @param id - Firestore document ID servisu
     * @param data - Pole k aktualizaci (bez `createdAt` a `createdBy`)
     */
    const edit = useCallback(async (id: string, data: VehicleServiceUpdater): Promise<void> => {
        const uid = auth.currentUser?.uid
        if (!uid) throw new Error('Cannot update vehicle service without authenticated user')
        await updateDoc(doc(firestore, COLLECTIONS.VEHICLE_SERVICE, id), {
            ...data,
            updatedAt: serverTimestamp(),
            updatedBy: uid,
        })
    }, [])

    return { get, list, create, edit }
}

export default useVehicleService
